import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { practicalInformationApi } from 'src/Services/API';
import {
  CreatePracticalInformationDto,
  PracticalInformationDetailDto,
  PracticalInformationFieldDtoFieldTypeEnum,
} from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';
import { trackPracticalInfoReminderTimeSet } from 'src/Services/Mixpanel/events';

export const useCreatePracticalInformation = (
  options?: UseMutationOptions<
    PracticalInformationDetailDto,
    unknown,
    CreatePracticalInformationDto
  >
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<PracticalInformationDetailDto, unknown, CreatePracticalInformationDto>(
    ['create-practical-information', assetId],
    async practicalInformation => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await practicalInformationApi.practicalInformationControllerCreate(
        assetId,
        practicalInformation
      );
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries('fetch-all-practical-information');
        trackEvent('PracticalInformation', 'create', data.title);

        if (data.reminderTimeOfDay) {
          trackPracticalInfoReminderTimeSet({
            practical_information_type: data.type,
            reminder_time_of_day: data.reminderTimeOfDay,
          });
        }

        if (
          data.fields.some(
            field =>
              field.fieldType === PracticalInformationFieldDtoFieldTypeEnum.Date ||
              field.fieldType === PracticalInformationFieldDtoFieldTypeEnum.Schedule
          )
        ) {
          queryClient.invalidateQueries({ queryKey: ['fetch-events'] });
        }

        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
